/*
* Developed for G.J. Gardner Homes by Softeq Development Corporation
* http://www.softeq.com
*/

import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { getUomQuantityType } from '@gh/builder-shared';
import { createUomQuantity, GtoUom, UomQuantityMapper } from '@gh/builder-shared-data';
import { createDialogRoute, FormState, FormStateBuilder } from '@gh/common-shared';
import { QuantityType } from '@gh/core-type';
import { DialogInput, NotificationService } from '@gh/core-ui';
import {
  getGraphicalTakeOffTopUom,
  GraphicalTakeOffBox,
  GraphicalTakeOffBoxDetails,
  GraphicalTakeOffTopView,
} from '../../resources/models';

export interface GraphicalTakeOffEditDialogConfig {
  top: GraphicalTakeOffTopView;
  dataBox: GraphicalTakeOffBox;
}

/**
 * Edit TOP component
 */
@Component({
  standalone: false,
  selector: 'gh-graphical-take-off-edit',
  templateUrl: 'graphical-take-off-edit.component.html',
  styleUrls: ['graphical-take-off-edit.component.scss'],
})
export class GraphicalTakeOffEditComponent implements OnInit, DialogInput<GraphicalTakeOffEditDialogConfig> {
  input: GraphicalTakeOffEditDialogConfig;

  valueType: QuantityType<any>;
  uom: GtoUom;
  isFeets: boolean;

  form: FormState<GraphicalTakeOffBoxDetails>;

  /**
   * Constructor
   *
   * @param {MatDialogRef<GraphicalTakeOffEditComponent>} dialogRef - Dialog ref
   * @param {NotificationService} notifications - Notification service
   * @param formStateBuilder
   * @returns {void}
   */
  constructor(
    private dialogRef: MatDialogRef<GraphicalTakeOffEditComponent>,
    private notifications: NotificationService,
    private formStateBuilder: FormStateBuilder,
  ) {
  }

  get uomLabel(): string {
    return this.input.top.takeOffParam.gtoUom.toLowerCase();
  }

  ngOnInit(): void {
    const { top, dataBox } = this.input;

    const gtoUom = getGraphicalTakeOffTopUom(top);
    this.uom = gtoUom;
    this.isFeets = gtoUom === GtoUom.Feets;

    this.valueType = getUomQuantityType(gtoUom);

    this.form = this.formStateBuilder.buildPlain({
      value: {
        ...dataBox,
        value: createUomQuantity(dataBox.value || 0, gtoUom),
      },
      children: {
        id: void 0,
        value: { required: true, type: this.valueType },
        label: { required: true },
        originalValue: void 0,
        point: void 0,
      },
    });
  }

  /**
   * Close dialog
   *
   * @returns {void}
   */
  close(): void {
    this.dialogRef.close();
  }

  /**
   * Apply clone
   *
   * @returns {void}
   */
  apply(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const dataBox = this.form.submitValue;

    this.dialogRef.close({
      ...dataBox,
      value: UomQuantityMapper.serialize(dataBox.value),
    });
  }
}

export const graphicalTakeOffEditDialogRoute = createDialogRoute({
  component: GraphicalTakeOffEditComponent,
});
